import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCustomerOperatorCallLogs } from '../../utils/callLogsApi';
import { fetchVoiceMktCustomerHistory } from '../../utils/marketingApi';
import { parseVoiceCallEntries } from '../../pages/customers/voiceCallSummary';
import { buildTrackerStyleFromCrmEntry, buildTrackerStyleSummary } from '../../pages/customers/voiceBotTrackerSummary';
import OperatorCallLogItem from './OperatorCallLogItem';
import CallSummaryItem from './CallSummaryItem';
import cd from '../../pages/customers/CustomerDetailsModal.module.scss';
import s from './CustomerCallHistorySplit.module.scss';

const OPS_LIMIT = 5;
const BOT_PAGE = 5;

export default function CustomerCallHistorySplit({
  customerId,
  phone,
  callSummary = null,
  showHeader = true,
  className = '',
}) {
  const [operatorLogs, setOperatorLogs] = useState([]);
  const [operatorTotal, setOperatorTotal] = useState(0);
  const [operatorLoading, setOperatorLoading] = useState(false);
  const [operatorError, setOperatorError] = useState('');

  const [voiceMktSummaries, setVoiceMktSummaries] = useState([]);
  const [voiceMktTotal, setVoiceMktTotal] = useState(0);
  const [voiceMktHasMore, setVoiceMktHasMore] = useState(false);
  const [voiceMktOffset, setVoiceMktOffset] = useState(0);
  const [voiceMktLoading, setVoiceMktLoading] = useState(false);
  const [voiceMktError, setVoiceMktError] = useState('');

  const loadOperator = useCallback(async () => {
    if (!customerId) {
      setOperatorLogs([]);
      setOperatorTotal(0);
      return;
    }
    setOperatorLoading(true);
    setOperatorError('');
    try {
      const data = await fetchCustomerOperatorCallLogs(customerId, { limit: OPS_LIMIT, offset: 0 });
      setOperatorLogs(data.logs || []);
      setOperatorTotal(data.total || 0);
    } catch (e) {
      setOperatorError(e.message || 'Failed to load call logs');
      setOperatorLogs([]);
    } finally {
      setOperatorLoading(false);
    }
  }, [customerId]);

  const loadVoiceMkt = useCallback(async (offset, append) => {
    if (!phone) {
      setVoiceMktSummaries([]);
      return;
    }
    setVoiceMktLoading(true);
    setVoiceMktError('');
    try {
      const data = await fetchVoiceMktCustomerHistory(phone, {
        summaryOffset: offset,
        summaryLimit: BOT_PAGE,
      });
      const chunk = data?.summaries || [];
      setVoiceMktSummaries((prev) => (append ? [...prev, ...chunk] : chunk));
      setVoiceMktTotal(Number(data?.total) || chunk.length);
      setVoiceMktHasMore(Boolean(data?.has_more));
      setVoiceMktOffset(offset);
    } catch (e) {
      setVoiceMktError(e.message || 'Failed to load voice bot history');
      if (!append) setVoiceMktSummaries([]);
    } finally {
      setVoiceMktLoading(false);
    }
  }, [phone]);

  useEffect(() => {
    loadOperator();
    loadVoiceMkt(0, false);
  }, [loadOperator, loadVoiceMkt]);

  const crmEntries = useMemo(
    () => [...parseVoiceCallEntries(callSummary)].reverse(),
    [callSummary],
  );
  const useMkt = voiceMktSummaries.length > 0;
  const botCount = useMkt ? (voiceMktTotal || voiceMktSummaries.length) : crmEntries.length;

  const body = (
    <div className={s.splitBody}>
      <div className={s.splitCol}>
        <div className={s.colHead}>
          <i className="fa fa-headphones" aria-hidden />
          <span>Operator Calls</span>
          {operatorTotal > 0 ? (
            <span className={cd.callTabCount}>{operatorTotal}</span>
          ) : null}
        </div>
        <div className={cd.callHistoryTabContent}>
          {operatorLoading ? (
            <div className={cd.callHistoryLoading}>
              <div className={cd.spinner} style={{ width: 28, height: 28 }} />
              <span>Loading call logs...</span>
            </div>
          ) : null}
          {operatorError ? (
            <div className={cd.callHistoryError}>
              <i className="fa fa-exclamation-triangle" />
              {operatorError}
            </div>
          ) : null}
          {!operatorLoading && !operatorError && !operatorLogs.length ? (
            <div className={cd.callHistoryEmpty}>
              No operator calls recorded for this customer.
            </div>
          ) : null}
          {!operatorLoading && !operatorError && operatorLogs.length > 0 ? (
            <ul className={cd.operatorCallList}>
              {operatorLogs.map((log) => (
                <OperatorCallLogItem
                  key={`${log.entry_kind || 'call_log'}-${log.id}`}
                  log={log}
                  customerId={customerId}
                />
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <div className={s.splitCol}>
        <div className={s.colHead}>
          <i className="fa fa-android" aria-hidden />
          <span>Voice Bot</span>
          {botCount > 0 ? <span className={cd.callTabCount}>{botCount}</span> : null}
        </div>
        <div className={cd.callHistoryTabContent}>
          {voiceMktLoading && !useMkt && !crmEntries.length ? (
            <div className={cd.callHistoryLoading}>
              <div className={cd.spinner} style={{ width: 28, height: 28 }} />
            </div>
          ) : null}
          {!useMkt && !crmEntries.length && !voiceMktLoading ? (
            <div className={cd.callHistoryEmpty}>No voice bot calls recorded yet.</div>
          ) : null}
          {useMkt ? (
            <ul className={cd.callSummaryList}>
              {voiceMktSummaries.map((row, index) => (
                <CallSummaryItem
                  key={`mkt-${row.id || index}-${row.time || ''}`}
                  entry={{ formatted: row.time || '', time: row.time || '' }}
                  trackerParts={buildTrackerStyleSummary(row)}
                />
              ))}
            </ul>
          ) : (
            <ul className={cd.callSummaryList}>
              {crmEntries.map((entry, index) => (
                <CallSummaryItem
                  key={`${entry.timestamp}-${entry.raw}-${index}`}
                  entry={entry}
                  trackerParts={buildTrackerStyleFromCrmEntry(entry)}
                />
              ))}
            </ul>
          )}
          {useMkt && voiceMktHasMore ? (
            <button
              type="button"
              className={cd.loadAllSummariesBtn}
              disabled={voiceMktLoading}
              onClick={() => loadVoiceMkt(voiceMktOffset + BOT_PAGE, true)}
            >
              <i className="fa fa-chevron-down" />
              Load more bot summaries
            </button>
          ) : null}
          {voiceMktError && !useMkt && crmEntries.length ? (
            <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
              Showing CRM copies (Voice Tracker API unreachable).
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  if (!showHeader) {
    return (
      <div className={`${s.root} ${className}`.trim()} onClick={(e) => e.stopPropagation()}>
        {body}
      </div>
    );
  }

  return (
    <div
      className={`${cd.callHistorySection} ${s.root} ${className}`.trim()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={cd.callHistoryHeader}>
        <div>
          <i className="fa fa-phone" aria-hidden />
          <span>Call History</span>
        </div>
      </div>
      {body}
    </div>
  );
}
